/**
 * Turns an unknown thrown value into a message that is safe to show a user.
 *
 * Deliberate user-facing messages (e.g. the retake cooldown) pass through
 * unchanged; raw Postgres / network / infra errors are replaced with a calm,
 * reassuring line. The raw error is always logged first for debuggability.
 */
const TECHNICAL_PATTERNS: RegExp[] = [
  /statement timeout/i,
  /canceling statement/i,
  /\b57014\b/,
  /failed to fetch/i,
  /fetch failed/i,
  /networkerror/i,
  /aborterror/i,
  /econnreset/i,
  /timeout/i,
  /pgrst/i,
  /\bjwt\b/i,
  /\b(500|502|503|504)\b/,
  /supabase/i,
  /\bat\s+\S+\s+\(/, // stack-trace-ish
  /\n\s+at\s/,
  // Internal error constants (e.g. RETAKE_COOLDOWN_ACTIVE) must never reach a
  // user — call sites translate the ones they own before falling back here.
  /\b[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+\b/,
  // Raw Postgres constraint / relation artefacts (lowercase English prose).
  /violates .*constraint/i,
  /new row for relation/i,
  /duplicate key value/i,
  /violates not-null constraint/i,
  /null value in column/i,
  /invalid input syntax/i,
  /"[a-z0-9_]+_(chk|fkey|key|pkey|idx)"/i,
];

const CALM = {
  ar: "الخدمة مزدحمة قليلاً الآن — إجاباتك محفوظة. المحاولة من جديد ممكنة بعد لحظات.",
  en: "Things are busy right now — your answers are saved. Please try again in a moment.",
} as const;

export function userErrorMessage(
  err: unknown,
  lang: "ar" | "en" = "ar",
  fallback?: string,
): string {
  console.error(err);

  const raw =
    typeof err === "string"
      ? err
      : err && typeof err === "object" && "message" in err
        ? String((err as { message?: unknown }).message ?? "")
        : "";

  const message = raw.trim();
  if (!message) return fallback ?? CALM[lang];
  if (TECHNICAL_PATTERNS.some((re) => re.test(message))) return CALM[lang];
  return message;
}
